import { useDebounce } from "shared";
import {
    ComponentTypes,
    ITemplateComponent,
    ITemplateSaveAndTicketComponent,
    templatesService,
    TicketField
} from "entities/template";
import { AppLang } from "app";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { useComponentsTranslations } from "../i18n";

const FIELD_GET_ATTEMPTS = 5;

export function useTrackerIntegration() {
    const getSaveAndTicketValue = (list: ITemplateComponent[]) => {
        const saveAndTicketComponent = list.find(c => c.type === ComponentTypes.saveAndTicket) as ITemplateSaveAndTicketComponent | undefined;
        return saveAndTicketComponent?.startrekQueue ? saveAndTicketComponent.startrekQueue : "";
    };
    const { i18n } = useTranslation();
    const t = useComponentsTranslations();
    const [saveAndTicketComponentAdded, setSaveAndTicketComponentAdded] = useState<boolean>(false);
    const [trackerIntegrationsCount, setTrackerIntegrationsCount] = useState<number>(0);
    const [trackerFieldsLoading, setTrackerFieldsLoading] = useState<boolean>(false);
    const [componentList, setComponentList] = useState<ITemplateComponent[]>([]);
    const [ticketFields, setTicketFields] = useState<TicketField[]>([]);
    const [startrekQueue, setStartrekQueue] = useState<string>(getSaveAndTicketValue(componentList));
    const debouncedStartrekQueue = useDebounce(startrekQueue, 1000);

    useEffect(() => {
        const startrekValue = getSaveAndTicketValue(componentList);
        const saveAndTicketComponent = componentList.find(c => c.type === ComponentTypes.saveAndTicket) as ITemplateSaveAndTicketComponent | undefined;
        const trackerIntegrationComponents = componentList.filter(c => c.type === ComponentTypes.trackerIntegration);

        setStartrekQueue(startrekValue);
        setSaveAndTicketComponentAdded(!!saveAndTicketComponent);
        setTrackerIntegrationsCount(trackerIntegrationComponents.length);
    }, [componentList]);

    useEffect(() => {
        if (!saveAndTicketComponentAdded && trackerIntegrationsCount > 0) {
            toast.error(t("noSaveAndTicketComponent"));
        }
    }, [saveAndTicketComponentAdded, trackerIntegrationsCount]);

    useEffect(() => {
        const abortController = new AbortController();

        if (debouncedStartrekQueue) {
            getTrackerFields(debouncedStartrekQueue, abortController.signal);
        } else {
            setTicketFields([]);
            setTrackerFieldsLoading(false);
        }

        return () => {
            abortController.abort("Change startrek queue");
        }
    }, [debouncedStartrekQueue, i18n.language]);

    const getTrackerFields = async (queue: string, signal: AbortSignal) => {
        let fields: TicketField[] = [];
        let attempt = 0;
        setTrackerFieldsLoading(true);
        while (attempt < FIELD_GET_ATTEMPTS && fields.length === 0 && !signal.aborted) {
            try {
                fields = await templatesService.loadTicketFields(i18n.language as AppLang, queue, {signal: signal});
            } catch (e) {
                console.error(e);
            }
            attempt += 1;
        }
        if (!signal.aborted) {
            if (fields.length === 0) {
                toast.error(t("trackerIntegrationNotWorking"));
            }
            setTicketFields(fields);
            setTrackerFieldsLoading(false);
        }
    };

    return {
        ticketFields,
        trackerFieldsLoading,
        setComponentList
    };
}
